import { useState, useEffect } from "react";
import styled from "styled-components";
import { motion, AnimatePresence } from "motion/react";

import Button from "Utilities/Button";
import useWizard from "./useWizard";
import { ACCENT_COLOR } from "./theme";

const STEP_LABELS = {
  1: "General Info",
  2: "Permission Release",
  3: "Student Info",
  4: "Pricing",
  5: "Confirmation",
};

function timeAgo(ts) {
  const seconds = Math.floor((Date.now() - ts) / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return "yesterday";
  return `${days}d ago`;
}

/* ─── Empty State (mirrors WizardIntro content) ─── */
function EmptyState({ onStart }) {
  return (
    <EmptyWrapper>
      <EmptyTop>
        <EmptyTitle className="merri">RE has never been easier.</EmptyTitle>
        <EmptyText className="raleway">
          We'll walk you through everything you need to get your Religious
          Education Signup setup. <em>RE</em>lax - we've got you!
        </EmptyText>
      </EmptyTop>
      <EmptyActions>
        <Button onClick={onStart}>
          Let's go <i className="fas fa-arrow-right" />
        </Button>
      </EmptyActions>
    </EmptyWrapper>
  );
}

/* ─── Session Card (shared between sections) ─── */
function SessionCard({ session, onResume, onDelete, onArchive, onUnarchive, isArchived }) {
  return (
    <CardWrapper
      layout
      exit={{
        opacity: 0,
        height: 0,
        marginBottom: 0,
        transition: {
          opacity: { duration: 0.2, ease: "easeOut" },
          height: { duration: 0.25, ease: "easeInOut", delay: 0.1 },
          marginBottom: { duration: 0.25, ease: "easeInOut", delay: 0.1 },
        },
      }}
    >
      <Card onClick={() => onResume(session.id)}>
        <CardTop className="card-content">
          <CardContent>
            <CardName className="raleway">{session.name}</CardName>
            <CardMeta>
              <CreatorName><i className="fas fa-user" /> Sarah Mitchell</CreatorName>
              {!session.finished && (
                <>
                  <Dot />
                  <span>Step {session.state.currentStep}: {STEP_LABELS[session.state.currentStep] || ""}</span>
                </>
              )}
              <Dot />
              <span>{timeAgo(session.updatedAt)}</span>
            </CardMeta>
          </CardContent>
          <CardActions className="card-actions">
            {isArchived ? (
              <ActionButton
                title="Unarchive"
                onClick={(e) => { e.stopPropagation(); onUnarchive(session.id); }}
              >
                <i className="fas fa-box-open" />
              </ActionButton>
            ) : session.finished && onArchive ? (
              <ActionButton
                title="Archive"
                onClick={(e) => { e.stopPropagation(); onArchive(session.id); }}
              >
                <i className="fas fa-box-archive" />
              </ActionButton>
            ) : null}
            <DeleteButton
              title="Delete"
              onClick={(e) => { e.stopPropagation(); onDelete(session.id); }}
            >
              <i className="fas fa-trash-can" />
            </DeleteButton>
          </CardActions>
        </CardTop>
        {!session.finished && (
          <ProgressBar>
            <ProgressFill style={{ width: `${(session.state.currentStep / 5) * 100}%` }} />
          </ProgressBar>
        )}
      </Card>
    </CardWrapper>
  );
}

/* ─── Main Component ─── */
export default function SessionPicker() {
  const {
    isOpen,
    getSessions,
    startNewSession,
    resumeSession,
    deleteSession,
    archiveSession,
    unarchiveSession,
    setShowOverview,
  } = useWizard();

  const [sessions, setSessions] = useState(() => getSessions());
  const [showArchived, setShowArchived] = useState(false);

  useEffect(() => {
    if (!isOpen) setSessions(getSessions());
  }, [isOpen, getSessions]);

  const refresh = () => setSessions(getSessions());

  const handleDelete = (id) => {
    deleteSession(id);
    refresh();
  };

  const handleArchive = (id) => {
    archiveSession(id);
    refresh();
  };

  const handleUnarchive = (id) => {
    unarchiveSession(id);
    refresh();
  };

  // "Let's go" opens the wizard straight to the overview screen
  const handleEmptyStart = () => {
    startNewSession();
    setShowOverview(true);
  };

  const activeSessions = sessions.filter((s) => s.finished && !s.archived);
  const inProgressSessions = sessions.filter((s) => !s.finished);
  const archivedSessions = sessions.filter((s) => s.archived);

  const hasAnySessions = sessions.length > 0;

  if (!hasAnySessions) {
    return <EmptyState onStart={handleEmptyStart} />;
  }

  return (
    <Wrapper>
      <Header>
        <Title className="merri">Your RE Setups</Title>
        <Button iconLeft onClick={startNewSession}>
          <i className="fas fa-circle-plus" style={{ marginRight: 2 }} /> New Setup
        </Button>
      </Header>

      <Content>
        {/* ── Active Section (no header — it's the default) ── */}
        {activeSessions.length > 0 && (
          <Section>
            <CardList>
              <AnimatePresence>
                {activeSessions.map((session) => (
                  <SessionCard
                    key={session.id}
                    session={session}
                    onResume={resumeSession}
                    onDelete={handleDelete}
                    onArchive={handleArchive}
                  />
                ))}
              </AnimatePresence>
            </CardList>
          </Section>
        )}

        {/* ── In Progress Section ── */}
        {inProgressSessions.length > 0 && (
          <Section>
            <SectionHeader>
              <SectionTitle className="raleway">Pick Up Where You Left Off</SectionTitle>
            </SectionHeader>
            <CardList>
              <AnimatePresence>
                {inProgressSessions.map((session) => (
                  <SessionCard
                    key={session.id}
                    session={session}
                    onResume={resumeSession}
                    onDelete={handleDelete}
                  />
                ))}
              </AnimatePresence>
            </CardList>
          </Section>
        )}

        {/* ── Archived Folder ── */}
        {archivedSessions.length > 0 && (
          <ArchivedSection>
            <ArchivedToggle onClick={() => setShowArchived(!showArchived)}>
              <ArchivedToggleInner>
                View Archived
                <ArchivedChevron
                  className="fas fa-chevron-right"
                  $open={showArchived}
                />
              </ArchivedToggleInner>
            </ArchivedToggle>
            <AnimatePresence>
              {showArchived && (
                <ArchivedList
                  as={motion.div}
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.25, ease: "easeInOut" }}
                >
                  <ArchivedInner>
                    <AnimatePresence>
                      {archivedSessions.map((session) => (
                        <SessionCard
                          key={session.id}
                          session={session}
                          onResume={resumeSession}
                          onDelete={handleDelete}
                          onUnarchive={handleUnarchive}
                          isArchived
                        />
                      ))}
                    </AnimatePresence>
                  </ArchivedInner>
                </ArchivedList>
              )}
            </AnimatePresence>
          </ArchivedSection>
        )}
      </Content>
    </Wrapper>
  );
}

/* ─── Empty State Styles ─── */
const EmptyWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 80px 64px;
`;

const EmptyTop = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 20px;
`;

const EmptyTitle = styled.div`
  font-size: 52px;
  color: #182941;
`;

const EmptyText = styled.div`
  font-size: 22px;
  color: #4b5563;
  max-width: 480px;
  line-height: 1.5;
`;

const EmptyActions = styled.div`
  display: flex;
  justify-content: flex-end;
`;

/* ─── Main Layout Styles ─── */
const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 48px 52px 0;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 36px;
  flex-shrink: 0;
`;

const Title = styled.div`
  font-size: 32px;
  color: #182941;
`;

const Content = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding-bottom: 48px;
`;

/* ─── Section Styles ─── */
const Section = styled.div`
  margin-bottom: 36px;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
  padding-left: 20px;
`;

const SectionTitle = styled.div`
  font-size: 14px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #6b7280;
`;

/* ─── Card Styles ─── */
const CardList = styled.div`
  display: flex;
  flex-direction: column;
`;

const CardWrapper = styled(motion.div)`
  overflow: hidden;
  margin-bottom: 12px;
`;

const Card = styled.div`
  display: flex;
  flex-direction: column;
  padding: 16px 20px;
  background: #fff;
  border: 1.5px solid #e5e7eb;
  border-radius: 12px;
  cursor: pointer;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  transition: border-color 0.2s, box-shadow 0.2s, background 0.15s;

  &:hover {
    border-color: ${ACCENT_COLOR};
    box-shadow: 0 3px 10px rgba(0, 0, 0, 0.08);

    .card-actions {
      opacity: 1;
    }
  }

  &:active {
    background: #f7f7f8;

    .card-content {
      transform: scale(0.98);
    }
  }
`;

const CardTop = styled.div`
  display: flex;
  align-items: center;
  transition: transform 0.15s;
`;

const CardContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const CardName = styled.div`
  font-size: 20px;
  font-weight: 600;
  color: #182941;
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CardMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #9ca3af;
`;

const CreatorName = styled.span`
  display: flex;
  align-items: center;
  gap: 5px;
  color: #6b7280;

  i {
    font-size: 10px;
    color: #9ca3af;
  }
`;

const Dot = styled.span`
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background: #d1d5db;
`;

const ProgressBar = styled.div`
  height: 4px;
  background: #e5e7eb;
  border-radius: 2px;
  overflow: hidden;
  margin-top: 12px;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: ${ACCENT_COLOR};
  border-radius: 2px;
  transition: width 0.3s ease;
`;

const CardActions = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  opacity: 0;
  transition: opacity 0.15s;
`;

const ActionButton = styled.button`
  appearance: none;
  border: none;
  background: none;
  cursor: pointer;
  font-size: 14px;
  color: #d1d5db;
  padding: 8px;
  border-radius: 6px;
  transition: color 0.15s, background 0.15s, transform 0.15s;

  &:hover {
    color: #6b7280;
    background: #f3f4f6;
  }

  &:active {
    transform: scale(0.85);
    color: #4b5563;
  }
`;

const DeleteButton = styled.button`
  appearance: none;
  border: none;
  background: none;
  cursor: pointer;
  font-size: 14px;
  color: #d1d5db;
  padding: 8px;
  border-radius: 6px;
  transition: color 0.15s, background 0.15s, transform 0.15s;

  &:hover {
    color: #ef4444;
    background: #fef2f2;
  }

  &:active {
    transform: scale(0.85);
    color: #dc2626;
  }
`;

/* ─── Archived Section ─── */
const ArchivedSection = styled.div`
  margin-bottom: 36px;
`;

const ArchivedToggle = styled.button`
  appearance: none;
  border: none;
  background: none;
  font-size: 15px;
  font-weight: 600;
  color: #6b7280;
  cursor: pointer;
  padding: 10px 14px 10px 20px;
  border-radius: 100px;
  font-family: inherit;
  display: block;
  transition: background 0.15s, color 0.15s;

  &:hover {
    background: rgba(0, 0, 0, 0.04);
    color: ${ACCENT_COLOR};
  }

  &:active {
    background: rgba(0, 0, 0, 0.07);
  }
`;

const ArchivedToggleInner = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  transition: transform 0.15s;

  ${ArchivedToggle}:active & {
    transform: scale(0.95);
  }
`;

const ArchivedChevron = styled.i`
  font-size: 12px;
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  transform: rotate(${(p) => (p.$open ? "90deg" : "0deg")});
`;

const ArchivedList = styled.div`
  overflow: hidden;
`;

const ArchivedInner = styled.div`
  padding-top: 16px;
  display: flex;
  flex-direction: column;
`;
